/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import PauseIcon from '@material-ui/icons/Pause';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import {toggleThemeStyle} from '../../../theme/helpers';
import {dlsColor} from '../../../theme/theme';
import {AssetUpdateProgressBar} from './AssetUpdateProgressBar';

// the auto-refresh subsystem's props (Play/Pause pill plus progress bar),
// grouped into one object since they're a single cohesive concern distinct
// from a header's own title-row sort/close props ...
export interface AssetUpdatesControlProps {
  isAssetUpdatesEnabled?: boolean;
  isDataLoading?: boolean;
  onAssetUpdatesPlayback?: (state: boolean) => void;
  onProgressCycleComplete?: () => void;
  updateCycleKey?: number;
}

interface AssetUpdatesControlComponentProps {
  updateProgress?: AssetUpdatesControlProps;
  height?: string;
}

interface UpdatesRowStyleProps {
  height: string;
}

interface PillHalfStyleProps {
  isActive: boolean;
}

// the Play/Pause icons share this geometry, differing only in color ...
const smallIconSize = {width: '16px', height: '16px'};

export const DEFAULT_UPDATE_PROGRESS: AssetUpdatesControlProps = {
  isAssetUpdatesEnabled : true,
  isDataLoading         : false,
  updateCycleKey        : 0
};

const useStyles = makeStyles(() => ({
  updatesRow : {
    boxSizing      : 'border-box',
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'flex-start',
    width          : '100%',
    height         : ({height}: UpdatesRowStyleProps) => height,
    padding        : '0 16px'
  },
  updatesCard : {
    boxSizing       : 'border-box',
    display         : 'flex',
    alignItems      : 'center',
    width           : '100%',
    maxWidth        : '424px',
    padding         : '4px 8px',
    gap             : '24px',
    backgroundColor : dlsColor('white'),
    borderRadius    : '12px'
  },
  pill : {
    display      : 'flex',
    flexShrink   : 0,
    width        : '96px',
    height       : '32px',
    borderRadius : '8px',
    filter       : 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.12))',
    overflow     : 'hidden'
  },
  progressWrapper : {
    flex     : '1 0 0',
    minWidth : 0
  }
}));

// split from `useStyles` so toggling isActive only recomputes this one
// rule, instead of the whole rule set being regenerated on every
// Play/Pause style lookup ...
const usePillHalfStyles = makeStyles(() => ({
  pillHalf : {
    display         : 'flex',
    flex            : '1 0 0',
    alignItems      : 'center',
    justifyContent  : 'center',
    border          : 'none',
    cursor          : 'pointer',
    backgroundColor : ({isActive}: PillHalfStyleProps) => isActive ? dlsColor('paleBlue') : dlsColor('grey6')
  }
}));

/**
 * AssetUpdatesControl renders the current theme's data-updates row: a
 * Play/Pause segmented pill plus the auto-refresh progress bar. Shared by
 * `AssetListSidePanelHeader` and `ResultsModule`'s current-theme branch -
 * `height` defaults to `AssetListSidePanelHeader`'s own two-row spec
 * (56px); `ResultsModule` overrides it to fit its single-row 44px layout.
 *
 * ```typescript
 *
 *   import {AssetUpdatesControl} from
 *     '@ion/dls/src/components/sidepanel/current/AssetUpdatesControl';
 *   ...
 *
 *   <AssetUpdatesControl updateProgress={{
 *     isAssetUpdatesEnabled,
 *     isDataLoading: isRefreshFetching,
 *     onAssetUpdatesPlayback,
 *     onProgressCycleComplete,
 *     updateCycleKey
 *   }} />
 *
 * ```
 *
 */
export const AssetUpdatesControl = ({
  updateProgress = DEFAULT_UPDATE_PROGRESS,
  height = '56px'
}: AssetUpdatesControlComponentProps) => {
  const {
    isAssetUpdatesEnabled = true,
    isDataLoading = false,
    onAssetUpdatesPlayback,
    onProgressCycleComplete,
    updateCycleKey = 0
  } = updateProgress;

  const classes              = useStyles({height});
  const playClasses          = usePillHalfStyles({isActive: isAssetUpdatesEnabled});
  const pauseClasses         = usePillHalfStyles({isActive: !isAssetUpdatesEnabled});
  const isProgressBarPlaying = isAssetUpdatesEnabled && !isDataLoading;

  return (
    <div className={classes.updatesRow}>
      <div className={classes.updatesCard}>
        <div className={classes.pill}>
          <button className={playClasses.pillHalf}
            onClick={function() {
              onAssetUpdatesPlayback?.(true);
            }}
            type='button'>
            <PlayArrowIcon style={{
              ...smallIconSize,
              color : isAssetUpdatesEnabled ? dlsColor('darkTeal') : dlsColor('grey1')
            }} />
          </button>
          <button className={pauseClasses.pillHalf}
            onClick={function() {
              onAssetUpdatesPlayback?.(false);
            }}
            type='button'>
            <PauseIcon style={{
              ...smallIconSize,
              color : !isAssetUpdatesEnabled ? dlsColor('darkTeal') : dlsColor('grey1')
            }} />
          </button>
        </div>
        <div className={classes.progressWrapper}>
          <AssetUpdateProgressBar isPlaying={isProgressBarPlaying}
            isFetching={isDataLoading}
            onCycleComplete={onProgressCycleComplete}
            cycleKey={updateCycleKey} />
        </div>
      </div>
    </div>
  );
};

export default AssetUpdatesControl;
